import * as React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  AccountPortalShell,
  HPrimaryButton,
  HGhostButton,
  SerifHeading,
  SectionLabel,
} from '../h';
import { buildWcCode, parseSsidsFromHash } from './wcCode';
import { buildRsCode, parseResetFidFromHash } from './rsCode';
import {
  WifiCredentialsForm,
  OTHER_SENTINEL,
  effectiveSsidFrom,
} from './WifiCredentialsForm';

/* HearthWifiView — the WiFi sign-in QR generator (PLAN P2.1, route
   /hearth/wifi). The customer lands here by scanning the QR on the Hearth's
   no-network screen; the URL fragment carries the networks the Hearth can see
   (never sent to any server — fragments stay in the browser). They pick their
   network, type the password, and show the generated code back to the Hearth.

   Design notes (the team's requirements, 12 Aug):
   - Same look and feel as the signed-in webapp (the `h` design system, guest
     flow idioms) even though this page is pre-auth and fully client-side.
   - Back-and-correct is first-class: ~40% of people mistype the password first
     time, so Back returns from the QR to the form with everything intact and
     the password inspectable.
   - The free-text inputs disable every platform text-mangling feature — a
     silently "corrected" SSID or password produces an undiagnosable failure
     on the TV (BUG-146 class).
   - Nothing here talks to a server: no endpoints, no analytics with form data.

   The form fields themselves live in WifiCredentialsForm (extracted for the
   WiFi-management change flow, PLAN wifi-change-webapp §4.3 — the two pages
   share one form; only the submit differs: render a QR here, encrypt there).

   The generated code's format is frozen in docs/imp-docs/no-network-wifi/
   QR-WC-FORMAT.md (WC v0). */

export default function HearthWifiView() {
  // Parsed from the URL the Hearth generated; a hash-less visit (typed URL,
  // shared link) simply starts with manual entry. Re-parsed on hashchange so a
  // freshly scanned QR landing in the same tab refreshes the picker.
  const [ssids, setSsids] = React.useState(() =>
    parseSsidsFromHash(window.location.hash),
  );

  // The Hearth's identity, from the `fid` parameter of its own QR. Absent for
  // anyone who reached this page without scanning a Hearth — and with no device
  // to scope a reset to, no reset is offered at all.
  const [resetFid, setResetFid] = React.useState(() =>
    parseResetFidFromHash(window.location.hash),
  );

  // 'form' | 'qr' | 'resetConfirm' | 'resetQr'
  const [stage, setStage] = React.useState('form');
  // Selection holds an index-string ('0', '1', …) or the sentinel — see
  // WifiCredentialsForm.
  const [selection, setSelection] = React.useState(
    ssids.length > 0 ? '0' : OTHER_SENTINEL,
  );
  const [manualSsid, setManualSsid] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');

  // On a fragment refresh (a freshly scanned QR landing in the same tab) just
  // replace the list. The chosen-network-survives-a-list-change reconciliation —
  // re-point the index by NAME, or drop to manual entry pre-filled with the vanished
  // name — lives in WifiCredentialsForm now, shared with the WiFi-management page.
  // Typed password always survives (it is separate state, untouched here).
  React.useEffect(() => {
    const onHashChange = () => {
      setSsids(parseSsidsFromHash(window.location.hash));
      // The reset flow is scoped to the Hearth named by the fragment, so the fid
      // has to follow a re-scan exactly as the network list does — otherwise a
      // second scan would offer a reset code built for the previous Hearth.
      setResetFid(parseResetFidFromHash(window.location.hash));
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const effectiveSsid = effectiveSsidFrom(ssids, selection, manualSsid);

  const handleContinue = (e) => {
    e.preventDefault();
    if (!effectiveSsid.trim()) {
      setError('Enter your WiFi network name.');
      return;
    }
    setError('');
    setStage('qr');
  };

  // Both reset stages depend on the fid. It can vanish under them: the hashchange
  // listener re-parses on every fragment change, so re-scanning a Hearth QR that
  // carries no fid would leave buildRsCode with null and throw during render —
  // blanking the page a customer is using to recover an already-broken device.
  // Losing the identity returns them to the form instead.
  if ((stage === 'resetConfirm' || stage === 'resetQr') && !resetFid) {
    setStage('form');
    return null;
  }

  if (stage === 'resetConfirm') {
    /* The ONLY confirmation anywhere in the reset flow: the Hearth does not ask
       again, because after hand control was removed it has no way to. Kept to a
       single line on purpose — someone reaching this screen has already tapped a
       link that says "Reset this Hearth", so the job here is to make the
       consequence unmissable, not to argue the case. The warning that the code
       keeps working afterwards belongs to the code itself and is on the next
       screen, next to the QR it describes. */
    return (
      <AccountPortalShell>
        <div className="px-4 pt-4 pb-6">
          <SectionLabel className="mb-1">Reset this Hearth</SectionLabel>
          <SerifHeading size="lg" className="mb-2">
            Are you sure?
          </SerifHeading>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
            This resets your Hearth. You&rsquo;ll need to set it up again
            afterwards.
          </p>
          <HPrimaryButton
            type="button"
            className="w-full mb-2"
            onClick={() => setStage('resetQr')}
          >
            Yes, make a reset code
          </HPrimaryButton>
          <HGhostButton type="button" className="w-full" onClick={() => setStage('form')}>
            No, go back
          </HGhostButton>
        </div>
      </AccountPortalShell>
    );
  }

  if (stage === 'resetQr') {
    return (
      <AccountPortalShell>
        <div className="px-4 pt-4 pb-6">
          <SectionLabel className="mb-1">Reset this Hearth</SectionLabel>
          <SerifHeading size="lg" className="mb-1.5">
            Show this code to your Hearth
          </SerifHeading>
          <p className="text-[13px] text-slate-500 leading-relaxed mb-3">
            Hold your phone up to the Hearth&rsquo;s camera. It will reset as
            soon as it sees this code — there is no further question to answer.
          </p>
          {/* Same sizing reasoning as the WiFi code: read by a TV camera across
              a room, so module size beats margin. */}
          <div className="-mx-4 mb-4">
            <div className="bg-white p-2 sm:rounded-xl sm:mx-4">
              <QRCodeSVG
                value={buildRsCode(resetFid)}
                marginSize={2}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          </div>
          <p className="text-[12px] text-slate-500 leading-relaxed mb-3">
            This code only works on your Hearth. Keep in mind it keeps working:
            anyone who has it can reset that Hearth by showing it the code.
          </p>
          <HGhostButton type="button" className="w-full" onClick={() => setStage('form')}>
            Back
          </HGhostButton>
        </div>
      </AccountPortalShell>
    );
  }

  if (stage === 'qr') {
    return (
      <AccountPortalShell>
        <div className="px-4 pt-4 pb-6">
          <SectionLabel className="mb-1">Hearth WiFi setup</SectionLabel>
          <SerifHeading size="lg" className="mb-1.5">
            Show this code to your Hearth
          </SerifHeading>
          <p className="text-[13px] text-slate-500 leading-relaxed mb-3">
            Hold your phone up to the Hearth&rsquo;s camera. The Hearth will
            tell you the moment it has seen the code.
          </p>
          {/* The code is held up to a TV camera across a room, so it must be as
              PHYSICALLY LARGE as the phone allows: the card runs edge to edge
              (negative margins cancel the page padding), the symbol's own margin
              is trimmed to 2 modules — under the QR spec's 4, a deliberate trade
              recorded in P2.1-SPEC: the card's p-2 supplies physical white around
              it, and module size matters more to this read than margin does — and
              the QR itself scales with the viewport rather than sitting at a fixed
              280px. On a narrow phone this roughly doubles the module size —
              the difference between holding the phone at arm's length and
              pressing it against the screen. */}
          <div className="-mx-4 mb-4">
            <div className="bg-white p-2 sm:rounded-xl sm:mx-4">
              <QRCodeSVG
                value={buildWcCode(effectiveSsid, password)}
                marginSize={2}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          </div>
          <p className="text-[13px] text-slate-500 leading-relaxed mb-4 text-center">
            Network: <span className="font-medium text-slate-700">{effectiveSsid}</span>
          </p>
          <HGhostButton type="button" className="w-full" onClick={() => setStage('form')}>
            Back — check or change the password
          </HGhostButton>
        </div>
      </AccountPortalShell>
    );
  }

  return (
    <AccountPortalShell>
      <form className="px-4 pt-4 pb-6" onSubmit={handleContinue}>
        <SectionLabel className="mb-2">Hearth WiFi setup</SectionLabel>
        <SerifHeading size="lg" className="mb-2">
          What&rsquo;s your WiFi?
        </SerifHeading>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-4">
          {ssids.length > 0
            ? 'These are the networks your Hearth can see. Pick yours and enter its password.'
            : 'Enter your WiFi network name exactly as it appears on your router, and its password.'}
        </p>

        <WifiCredentialsForm
          ssids={ssids}
          selection={selection}
          onSelectionChange={setSelection}
          manualSsid={manualSsid}
          onManualSsidChange={setManualSsid}
          password={password}
          onPasswordChange={setPassword}
          idPrefix="hw"
        />

        {error && (
          <p className="text-[13px] text-red-600 leading-relaxed mb-3">{error}</p>
        )}

        <HPrimaryButton type="submit" className="w-full">
          Continue
        </HPrimaryButton>

        {/* Deliberately small and last: resetting is irreversible and is not what
            almost anyone arriving here wants. Hidden entirely without a fid —
            with no device identity there is nothing to scope a reset code to. */}
        {resetFid && (
          <p className="text-center mt-5">
            <button
              type="button"
              className="text-[12px] text-slate-400 underline underline-offset-2 hover:text-slate-600"
              onClick={() => setStage('resetConfirm')}
            >
              Reset this Hearth
            </button>
          </p>
        )}
      </form>
    </AccountPortalShell>
  );
}
